import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from '../../../../Flux/index.js'
import type { State } from '../../../../Flux/Slice/index.js'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import { BlastSource, BlastSourcesRecord, TaxaBlastSources } from '../../../../Model/Project/BlastSource.js'
import type { COUNTMODE, SCALINGMODE, SourceId, VizSourceEntity } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import type { Taxas } from '../../../../Model/ITaxa.js'
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid/index.js'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.tsx'
import NatureKPIPanel from './NatureKPIPanel.tsx'
import SpinPanel from './SpinPanel.tsx'
import { trackVizSourceToggle } from '../../../../Services/Telemetry/index.js'

const ALL_BLAST_SOURCES = Object.keys(BlastSource) as Array<keyof typeof BlastSource>

export default function SourceKnobsPanel({ sourceId }: { sourceId: SourceId }) {
  const dispatch = useDispatch()

  const src: VizSourceEntity | null = useSelector((s: typeof State) => s.visualization.sources[sourceId] ?? null)
  const status = src?.status ?? 'init'
  const shared = src?.shared

  const contextTargetTaxonomy: Taxas = useSelector((s: typeof State) => s.visualization.context?.targetTaxonomy ?? 'CO1_ANIMAL')

  const availableBlastSources = useMemo(() => {
    return (TaxaBlastSources[contextTargetTaxonomy] ?? ALL_BLAST_SOURCES) as Array<keyof typeof BlastSource>
  }, [contextTargetTaxonomy])

  // primitives with stable fallbacks
  const gradeThreshold = shared?.gradeThreshold ?? 95
  const countMode = (shared?.countMode ?? 'READS') as COUNTMODE
  const scalingMode = (shared?.scalingMode ?? 'ABSOLUTE') as SCALINGMODE

  const blastType = (shared?.blastType ?? availableBlastSources[0] ?? BlastSource.NCBI) as keyof typeof BlastSource

  const busy = isLoadingState(status)

  // local UI state for grade slider (debounced commit)
  const [gradeRaw, setGradeRaw] = useState<number>(gradeThreshold)

  useEffect(() => {
    setGradeRaw(gradeThreshold)
  }, [gradeThreshold])

  useEffect(() => {
    const id = setTimeout(() => {
      if (gradeThreshold !== gradeRaw) {
        dispatch(visualizationSlice.actions.updateSourceShared({ sourceId, patch: { gradeThreshold: gradeRaw } }))
      }
    }, 300)
    return () => clearTimeout(id)
  }, [gradeRaw, gradeThreshold, dispatch, sourceId])

  const setCountMode = (cm: COUNTMODE) => {
    trackVizSourceToggle('count_mode', cm)
    dispatch(visualizationSlice.actions.updateSourceShared({ sourceId, patch: { countMode: cm } }))
  }

  const setBlast = (b: BlastSource) => {
    trackVizSourceToggle('blast_source', b)
    dispatch(visualizationSlice.actions.updateSourceShared({ sourceId, patch: { blastType: b } }))
  }

  const PRESETS: Array<{ v: number; label: string }> = useMemo(
    () => [
      { v: 75, label: 'Phylum' },
      { v: 80, label: 'Class' },
      { v: 85, label: 'Order' },
      { v: 90, label: 'Family' },
      { v: 95, label: 'Genus' },
      { v: 97, label: 'Species' }
    ],
    []
  )

  return (
    <div className="flex flex-col gap-4 rounded p-4 shadow">
      {/* Count mode */}
      <div className="flex items-center gap-3 flex-wrap">
        <label htmlFor="readcount-select" className="font-semibold">
          Read Count Mode:
        </label>
        <select
          id="readcount-select"
          data-testid="knob-countmode"
          className="select select-bordered select-sm"
          value={countMode}
          onChange={(e) => setCountMode(e.target.value as COUNTMODE)}
          disabled={busy}
        >
          <option value="READS">Reads</option>
          <option value="UNIQUE">Unique</option>
        </select>
      </div>

      {/* Grade */}
      <div className="flex items-center gap-3 flex-wrap">
        <label htmlFor="grade-threshold-slider" className="font-semibold">
          Grade Threshold:
        </label>
        <input
          id="grade-threshold-slider"
          type="range"
          min={75}
          max={100}
          step={0.1}
          value={gradeRaw}
          onChange={(e) => setGradeRaw(Number(e.target.value))}
          className="range range-success w-56"
          disabled={busy}
        />
        <input
          type="number"
          className="font-mono text-sm input input-number w-24 input-ghost"
          value={gradeRaw.toFixed(1)}
          onChange={(e) => setGradeRaw(Number(e.target.value))}
          disabled={busy}
        />
        <div className="flex gap-2 flex-wrap">
          {PRESETS.map((p) => (
            <button
              key={p.v}
              className={`btn btn-xs ${gradeRaw === p.v ? 'btn-active btn-success' : 'btn-outline btn-success'}`}
              onClick={() => setGradeRaw(p.v)}
              disabled={busy}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Blast source */}
      <div className="flex items-center gap-3 flex-wrap">
        <label htmlFor="blast-select" className="font-semibold">
          Blast Source:{' '}
          {(blastType === 'RDP' || blastType === 'Consensus') && (
            <span className={'text-xs text-red font-normal'}>
              <TooltipTrigger tooltip={`Blast Source ${blastType} falls back to NCBI for grade determination`}>
                <ExclamationTriangleIcon className={'w-4 h-4 text-warning inline'} />
              </TooltipTrigger>
            </span>
          )}
        </label>
        <select
          id="blast-select"
          className="select select-bordered select-sm"
          value={blastType}
          onChange={(e) => setBlast(BlastSourcesRecord[e.target.value])}
          disabled={busy}
        >
          {ALL_BLAST_SOURCES.map((key) => (
            <option key={key} value={key} disabled={!availableBlastSources.includes(key)}>
              {key}
            </option>
          ))}
        </select>
        {contextTargetTaxonomy && <span className="text-xs opacity-60">(taxonomy: {String(contextTargetTaxonomy)})</span>}
      </div>
      <div className="flex items-center gap-3">
        <label className="font-semibold">Normalize (percentage)</label>
        <input
          type="checkbox"
          data-testid="knob-normalize"
          className="toggle toggle-success"
          checked={scalingMode === 'PERCENTAGE'}
          onChange={(e) => {
            trackVizSourceToggle('scaling', e.target.checked ? 'PERCENTAGE' : 'ABSOLUTE')
            dispatch(
              visualizationSlice.actions.updateSourceShared({
                sourceId,
                patch: { scalingMode: e.target.checked ? 'PERCENTAGE' : 'ABSOLUTE' }
              })
            )
          }}
          disabled={busy}
        />
      </div>

      <NatureKPIPanel sourceId={sourceId} />

      <SpinPanel sourceId={sourceId} />
    </div>
  )
}
